// Pointer coordinate correction for a CSS-rotated ancestor. Pure, no DOM.
//
// The AR HUD counter-rotates by 0/90/180/270 deg (CSS rotate(), clockwise on screen). Pointer events
// report viewport (client) coordinates, so they have to be rotated back into the element's local,
// unrotated frame. getBoundingClientRect() of a rotated element is the axis-aligned box around it;
// its centre is the element centre for any rotation, which makes this independent of the ancestor's
// transform-origin.

import type { Point } from './mercator';

export interface ClientRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** Normalise any angle to [0, 360). */
export function normDeg(deg: number): number {
  const d = deg % 360;
  return d < 0 ? d + 360 : d;
}

/**
 * Client (viewport) coordinates -> element-local CSS pixels.
 * rect = element.getBoundingClientRect(); localW/localH = the element's untransformed size
 * (clientWidth/clientHeight); deg = CSS rotation of the ancestor (clockwise).
 */
export function clientToLocal(
  clientX: number,
  clientY: number,
  rect: ClientRect,
  localW: number,
  localH: number,
  deg: number,
  out: Point,
): Point {
  const dx = clientX - (rect.left + rect.width / 2);
  const dy = clientY - (rect.top + rect.height / 2);
  const [c, s] = cosSin(deg);
  // local = R(-deg) * d, with CSS rotate(deg): screen = R(deg) * local, R(a) = [cos -sin; sin cos].
  out.x = localW / 2 + dx * c + dy * s;
  out.y = localH / 2 - dx * s + dy * c;
  return out;
}

/** Rotate a client-space delta into the local frame. */
export function clientDeltaToLocal(dx: number, dy: number, deg: number, out: Point): Point {
  const [c, s] = cosSin(deg);
  out.x = dx * c + dy * s;
  out.y = -dx * s + dy * c;
  return out;
}

/** Exact values for multiples of 90 deg (avoids 6e-17 noise), trig otherwise. */
function cosSin(deg: number): [number, number] {
  const d = normDeg(deg);
  if (d === 0) return [1, 0];
  if (d === 90) return [0, 1];
  if (d === 180) return [-1, 0];
  if (d === 270) return [0, -1];
  const r = (d * Math.PI) / 180;
  return [Math.cos(r), Math.sin(r)];
}
